import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { AdminUser, useUpdateUserStatus } from '@/services/admin/users.service'
import { getErrorMessage } from '@/utils/error'

interface EditUserDialogProps {
  user: AdminUser
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditUserDialog({ user, open, onOpenChange }: EditUserDialogProps) {
  const { toast } = useToast()
  const [isActive, setIsActive] = useState(user.active)
  const updateStatusMutation = useUpdateUserStatus()

  useEffect(() => {
    if (open) {
      setIsActive(user.active)
    }
  }, [open, user.active])

  const handleSubmit = async () => {
    if (isActive === user.active) {
      // Nada mudou
      onOpenChange(false)
      return
    }

    try {
      await updateStatusMutation.mutateAsync({
        userId: user.id,
        data: { active: isActive },
      })

      toast({
        title: 'Status atualizado',
        description: `${user.name} foi ${isActive ? 'ativado' : 'desativado'} com sucesso.`,
      })

      onOpenChange(false)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar status',
        description: getErrorMessage(error, 'Ocorreu um erro ao atualizar o status.'),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Status do Usuário</DialogTitle>
          <DialogDescription>
            <div className="mt-2 space-y-1">
              <p className="font-semibold text-foreground">👤 {user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="border-t border-border/50 my-4" />

        <div className="space-y-4">
          <div className="flex items-center justify-between space-x-4">
            <div className="space-y-1">
              <Label htmlFor="active-status" className="text-sm font-medium">
                Status do Usuário
              </Label>
              <p className="text-sm text-muted-foreground">
                {isActive
                  ? '✅ Usuário pode acessar a plataforma'
                  : '🚫 Usuário não pode fazer login'}
              </p>
            </div>
            <Switch id="active-status" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          {!isActive && (
            <div className="bg-muted/50 border border-border/50 rounded-lg p-3 text-sm">
              <p className="text-muted-foreground">
                ⚠️ <strong>Atenção:</strong> Desativar um usuário irá impedi-lo de fazer login, mas
                seu progresso e matrículas serão preservados.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isActive === user.active || updateStatusMutation.isPending}
          >
            {updateStatusMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
